/**
 * The quotes this user opened last (docs/audits/PILOT-QUOTES-SHELL.md §4.1, §5.2): the
 * flyout's Recents. Since S4 they are `quotes.recents` in the user's server document — up to
 * 10 quote ids, newest first, one entry per quote — so they follow the login to any device.
 *
 * The document holds ids only. The labels (quote number, customer) come from a per-user
 * display cache filled when a quote is opened; on a device that has not seen a quote yet the
 * flyout fetches its label (QuoteViewsMenu).
 *
 * Customers polish 13 (mirrored in customers/recentCustomers.js): every label write bumps a label
 * version that each mounted useRecentQuotes follows, so a label written for the id already first
 * in recents (whose ids did not change) shows at once, in the writer and the flyout alike.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { usePreferences } from '../../contexts/PreferencesContext';
import { readGuarded, writeGuarded } from '../../utils/guardedStorage';
import { MAX_RECENTS } from '../../contexts/preferenceShapes';

export const RECENT_QUOTES_LIMIT = MAX_RECENTS;

const labelsKey = (userId) => `crm.ui.prefs.${userId}.quoteLabels.v1`;
const isLabelMap = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

// The label version: bumped on every write, followed by every mounted hook.
let labelVersion = 0;
const labelListeners = new Set();
const subscribeLabels = (listener) => {
  labelListeners.add(listener);
  return () => labelListeners.delete(listener);
};
const getLabelVersion = () => labelVersion;

export function readQuoteLabels(userId) {
  return userId ? readGuarded(labelsKey(userId), isLabelMap, {}) : {};
}

export function rememberQuoteLabel(userId, quote) {
  const id = Number(quote && quote.id);
  if (!userId || !Number.isInteger(id) || id <= 0) return;
  const labels = readQuoteLabels(userId);
  labels[id] = {
    quote_number: quote.quote_number || quote.quotation_number || null,
    customer_name: quote.customer_name || null
  };
  // Bounded: a display cache, not a record (the 50 highest ids are kept).
  const keep = Object.keys(labels).slice(-50);
  writeGuarded(labelsKey(userId), Object.fromEntries(keep.map((k) => [k, labels[k]])));
  labelVersion += 1;
  labelListeners.forEach((l) => l());
}

/**
 * useRecentQuotes() -> { recents: [{ id, quote_number?, customer_name? }], remember(quote) }
 */
export function useRecentQuotes() {
  const { doc, update, userId } = usePreferences('quotes');
  const ids = doc.recents;
  const version = useSyncExternalStore(subscribeLabels, getLabelVersion, getLabelVersion);

  const recents = useMemo(() => {
    const labels = readQuoteLabels(userId);
    return (ids || []).map((id) => ({ id, ...(labels[id] || {}) }));
    // `version`: a label write re-reads the cache although the ids are the same.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, userId, version]);

  const remember = useCallback((quote) => {
    const id = Number(quote && quote.id);
    if (!Number.isInteger(id) || id <= 0) return;
    rememberQuoteLabel(userId, quote);
    // Re-opening the newest one (a reload after an action) changes nothing: no write.
    if (ids && ids[0] === id) return;
    update((d) => ({
      ...d,
      recents: [id, ...(d.recents || []).filter((x) => x !== id)].slice(0, RECENT_QUOTES_LIMIT)
    }));
  }, [ids, update, userId]);

  return { recents, remember };
}
